import React, { ReactNode, useEffect, useRef, useState } from 'react';
import { XMarkIcon, ArrowsPointingOutIcon, ArrowsPointingInIcon } from '@heroicons/react/24/outline';
import { twMerge } from 'tailwind-merge';
import clsx from 'clsx';

interface AsideProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  description?: string;
  children: ReactNode;
  width?: string;
  className?: string;
  /** Extra controls (e.g. a kebab actions menu) rendered next to the close button */
  actions?: ReactNode;
}

export const Aside: React.FC<AsideProps> = ({
  isOpen,
  onClose,
  title,
  description,
  children,
  width = '500px',
  className,
  actions,
}) => {
  const asideRef = useRef<HTMLDivElement>(null);
  const [isFullScreen, setIsFullScreen] = useState(false);

  // Close on ESC key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  // Lock body scroll when aside is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  // Reset full-screen state whenever the aside closes
  useEffect(() => {
    if (!isOpen) setIsFullScreen(false);
  }, [isOpen]);

  if (!isOpen) return null;

  const resolvedWidth = isFullScreen || window.innerWidth < 768 ? '100vw' : width;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 z-40 transition-opacity"
        onClick={onClose}
      />

      {/* Aside panel */}
      <div
        ref={asideRef}
        className={twMerge(
          clsx(
            'fixed top-0 left-0 h-full bg-white dark:bg-gray-800 shadow-xl z-50',
            'transform transition-transform duration-300 ease-in-out',
            'flex flex-col',
            'w-full md:w-auto',
            isOpen ? 'translate-x-0' : '-translate-x-full',
            className
          )
        )}
        style={{ width: resolvedWidth, maxWidth: '100vw' }}
      >
        {/* Header */}
        <div className="flex items-start justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
          <div className="flex-1 min-w-0">
            {title && (
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white truncate" title={title}>
                {title}
              </h2>
            )}
            {description && (
              <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                {description}
              </p>
            )}
          </div>
          <div className="flex items-center gap-1 ml-4 flex-shrink-0">
            {actions}
            <button
              onClick={() => setIsFullScreen((prev) => !prev)}
              className="hidden md:inline-flex p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
              title={isFullScreen ? 'Exit full screen' : 'Full screen'}
            >
              {isFullScreen ? (
                <ArrowsPointingInIcon className="h-5 w-5" />
              ) : (
                <ArrowsPointingOutIcon className="h-5 w-5" />
              )}
            </button>
            <button
              onClick={onClose}
              className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {children}
        </div>
      </div>
    </>
  );
};
